import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/typed";
import { downloadFile, errMsg } from "@/lib/api";

type Doc = { key: string; label: string; hint: string; show: boolean; on: boolean };

export function StatePackageButton({ c }: { c: any }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const atts = (c.attachments || []).filter((a: any) => !a.is_deleted);
  const hasState = atts.some((a: any) => a.kind === "state_authorization");
  const ny = (c.data?.addresses || []).some((a: any) => a.state === "NY" || a.state === "New York");
  const ca = (c.data?.addresses || []).some((a: any) => a.state === "CA" || a.state === "California");
  const docs: Doc[] = [
    { key: "disclosure", label: "Signed disclosure and authorization", hint: "with the applicant's signatures, IP and date", show: true, on: true },
    { key: "state_auth", label: "Signed State Authorization", hint: "on file for this applicant", show: hasState, on: true },
    { key: "payment_auth", label: "Credit card authorization", hint: "signed at payment", show: !!c.payment_auth, on: false },
    { key: "fcra", label: "A Summary of Your Rights Under the FCRA", hint: "2 pages", show: true, on: true },
    { key: "ny_23a", label: "NY Correction Law Article 23-A", hint: ny ? "applicant has a New York address" : "2 pages", show: true, on: ny },
    { key: "ca", label: "California Notice Regarding Background Investigation", hint: ca ? "applicant has a California address" : "1 page", show: true, on: ca },
    { key: "privacy", label: "InfoRetrieval Services Privacy Policy", hint: "1 page", show: true, on: false },
  ].filter((d) => d.show);
  const [sel, setSel] = useState<Record<string, boolean>>(() => Object.fromEntries(docs.map((d) => [d.key, d.on])));
  const picked = docs.filter((d) => sel[d.key]).map((d) => d.key);
  const p = c.data?.personal || {};
  const go = async () => {
    setBusy(true);
    try {
      await downloadFile(`/admin/cases/${c.id}/state-package.pdf?parts=${picked.join(",")}`, `Authorization-Package-${p.last_name || ""}-${p.first_name || ""}.pdf`.replace(/[^A-Za-z0-9.-]+/g, "-"));
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <>
      <button className="btn-secondary btn-sm" data-testid="case-state-package-button" title="Pick the signed forms and notices to send to a state that requires the applicant's authorization" onClick={() => setOpen(true)}>
        <Package className="h-4 w-4" /> State package
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="state-package-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Authorization package for state submission</DialogTitle>
            <DialogDescription className="text-[15px]">Choose what goes in the PDF. Documents are combined in the order shown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {docs.map((d) => (
              <label key={d.key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#1B365D]/40">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#1B365D]" data-testid={`state-package-${d.key}`} checked={!!sel[d.key]} onChange={(e) => setSel({ ...sel, [d.key]: e.target.checked })} />
                <span><span className="block text-sm font-semibold text-[#1A2536]">{d.label}</span><span className="text-xs text-[#64748B]">{d.hint}</span></span>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <button className="btn-secondary btn-sm" onClick={() => setOpen(false)} data-testid="state-package-cancel">Cancel</button>
            <button className="btn-primary btn-sm" disabled={busy || !picked.length} onClick={go} data-testid="state-package-download">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Download package
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
